import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { QrCode, CheckCircle, Calendar, MapPin, Users, ArrowRight, Star } from "lucide-react";
import { Input } from "@/components/ui/input";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5 } }) };

export default function EventCheckinPage() {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [checkedIn, setCheckedIn] = useState(false);
  const [attendance, setAttendance] = useState(0);

  useEffect(() => {
    axios.get(`${API}/events/${eventId}`).then(r => {
      setEvent(r.data);
      setAttendance(r.data.attendance || 0);
    }).catch(() => toast.error("Event not found")).finally(() => setLoading(false));
  }, [eventId]);

  const handleCheckin = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) { toast.error("Name and email are required."); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/events/${eventId}/checkin`, { guest_name: name, guest_email: email });
      if (res.data.already) {
        toast.info("You're already checked in! Enjoy the event.");
      } else {
        toast.success("Welcome! You're checked in.");
        setAttendance(a => a + 1);
      }
      setCheckedIn(true);
      localStorage.setItem(`checkin_${eventId}`, email.toLowerCase());
    } catch {
      toast.error("Check-in failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="pt-20 min-h-screen bg-luxury-black flex items-center justify-center"><div className="text-gold animate-pulse font-body">Loading...</div></div>;
  if (!event) return null;

  return (
    <div data-testid="event-checkin-page" className="pt-20 min-h-screen bg-luxury-black">
      {event.cover_image && (
        <div className="relative h-[35vh] overflow-hidden">
          <img src={event.cover_image} alt={event.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-luxury-black via-luxury-black/60 to-transparent" />
        </div>
      )}

      <div className="max-w-2xl mx-auto px-6 py-10 -mt-16 relative z-10">
        <motion.div initial="hidden" animate="visible" className="space-y-8">
          {/* Event Info Card */}
          <motion.div variants={fadeUp} custom={0} className="glass-card p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-gold/15 flex items-center justify-center">
                <QrCode size={20} className="text-gold" />
              </div>
              <span className="text-gold/60 text-[10px] font-body uppercase tracking-[0.2em]">Event Check-In</span>
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl text-white mb-3" data-testid="checkin-event-title">{event.title}</h1>
            {event.description && <p className="text-white/50 font-body text-sm leading-relaxed mb-5">{event.description}</p>}
            <div className="flex flex-wrap gap-5 text-white/40 text-xs font-body">
              {event.event_date && <span className="flex items-center gap-2"><Calendar size={13} className="text-gold" /> {event.event_date}</span>}
              {event.location && <span className="flex items-center gap-2"><MapPin size={13} className="text-gold" /> {event.location}</span>}
              <span className="flex items-center gap-2"><Users size={13} className="text-gold" /> {attendance} checked in</span>
              {event.avg_rating > 0 && <span className="flex items-center gap-2"><Star size={13} className="text-gold fill-gold" /> {event.avg_rating}/5</span>}
            </div>
          </motion.div>

          {/* Check-in Form or Success */}
          {!checkedIn ? (
            <motion.form variants={fadeUp} custom={1} onSubmit={handleCheckin} className="glass-card p-8 space-y-6" data-testid="checkin-form">
              <h2 className="font-heading text-xl text-white">Welcome, Guest</h2>
              <p className="text-white/40 font-body text-sm">Enter your details to check in. After the event, you'll be able to leave a review and share your experience.</p>
              <div className="space-y-4">
                <div>
                  <label className="text-white/50 text-xs font-body uppercase tracking-wider mb-2 block">Your Name *</label>
                  <Input data-testid="checkin-name" value={name} onChange={e => setName(e.target.value)} placeholder="Full name" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 rounded-sm" />
                </div>
                <div>
                  <label className="text-white/50 text-xs font-body uppercase tracking-wider mb-2 block">Email Address *</label>
                  <Input data-testid="checkin-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 rounded-sm" />
                </div>
              </div>
              <button type="submit" disabled={submitting} data-testid="checkin-submit" className="w-full inline-flex items-center justify-center gap-2 bg-gold text-black font-body font-medium text-sm px-8 py-4 hover:bg-gold-light transition-colors disabled:opacity-50">
                {submitting ? "Checking in..." : "Check In"} <ArrowRight size={16} />
              </button>
            </motion.form>
          ) : (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }} className="glass-card p-10 text-center" data-testid="checkin-success">
              <CheckCircle size={48} className="text-gold mx-auto mb-4" />
              <h2 className="font-heading text-2xl text-white mb-2">You're In!</h2>
              <p className="text-white/50 font-body text-sm mb-6">Enjoy {event.title}. After the event, visit the event page to leave your review.</p>
              <Link to={`/events/${eventId}`} data-testid="checkin-view-event" className="inline-flex items-center gap-2 bg-white/5 border border-white/10 text-white/70 hover:text-gold hover:border-gold/30 font-body text-sm px-6 py-3 transition-colors">
                View Event Page <ArrowRight size={14} />
              </Link>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
